import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import {
  Plus,
  Trash2,
  Clock,
  GripVertical,
  Check,
  ChevronsUpDown,
  Bot,
} from "lucide-react"
import { InterviewQuestion } from "@/modules/public/dto/videoInterviewDto"
import { useApplicants } from "../hooks/useApplicants"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/modules/shared/ui/components/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/modules/shared/ui/components/command"
import { cn } from "@/lib/utils"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import { generateAiQuestions } from "../../applicants-service"
import { toast } from "sonner"

interface CreateVideoInterviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (data: { questions: Partial<InterviewQuestion>[] }) => void
  isSubmitting: boolean
  vacancyId?: string
  organizationId?: string
}

export function CreateVideoInterviewDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  vacancyId,
  organizationId,
}: CreateVideoInterviewDialogProps) {
  const { getQuestions } = useApplicants()
  const [availableQuestions, setAvailableQuestions] = useState<
    InterviewQuestion[]
  >([])
  const [isGeneratingAi, setIsGeneratingAi] = useState(false)
  const [questions, setQuestions] = useState<Partial<InterviewQuestion>[]>([
    { text: "", timeLimitSeconds: 120, isMandatory: true, order: 1 },
  ])

  useEffect(() => {
    if (open) {
      getQuestions().then(setAvailableQuestions)
    }
  }, [open, getQuestions])

  const handleAddQuestion = () => {
    if (questions.length >= 5) {
      toast.error("Maximum 5 questions allowed.")
      return
    }
    const newQuestions = [
      { text: "", timeLimitSeconds: 120, isMandatory: true, order: 0 },
      ...questions,
    ]
    newQuestions.forEach((q, i) => (q.order = i + 1))
    setQuestions(newQuestions)
  }

  const handleRemoveQuestion = (index: number) => {
    const newQuestions = [...questions]
    newQuestions.splice(index, 1)
    // Re-order
    newQuestions.forEach((q, i) => (q.order = i + 1))
    setQuestions(newQuestions)
  }

  const updateQuestion = (
    index: number,
    field: keyof InterviewQuestion,
    value: string | number | boolean,
  ) => {
    const newQuestions = [...questions]
    newQuestions[index] = { ...newQuestions[index], [field]: value }
    setQuestions(newQuestions)
  }

  const handleSubmit = () => {
    if (
      questions.some(
        (q: Partial<InterviewQuestion>) => !q.text || !q.timeLimitSeconds,
      )
    )
      return
    onConfirm({ questions })
  }

  const handleGenerateAi = async () => {
    if (!vacancyId || !organizationId) return

    setIsGeneratingAi(true)
    try {
      const aiQuestions = await generateAiQuestions(organizationId, vacancyId)

      // Remove empty placeholders
      const validQuestions = questions.filter(
        (q) => q.text && q.text.trim() !== "",
      )

      const slotsAvailable = 5 - validQuestions.length
      if (slotsAvailable <= 0) {
        toast.error("Maximum of 5 valid questions already added.")
        setQuestions(validQuestions)
        return
      }

      const questionsToAdd = aiQuestions.slice(0, slotsAvailable)
      const mappedQuestions = questionsToAdd.map(
        (q: Partial<InterviewQuestion>) => ({
          text: q.text || "",
          timeLimitSeconds: q.timeLimitSeconds || 120,
          isMandatory: q.isMandatory !== undefined ? q.isMandatory : true,
          order: 0,
        }),
      )

      const finalQuestions = [...mappedQuestions, ...validQuestions]
      finalQuestions.forEach((q, i) => (q.order = i + 1))

      setQuestions(
        finalQuestions.length > 0
          ? finalQuestions
          : [{ text: "", timeLimitSeconds: 120, isMandatory: true, order: 1 }],
      )
      toast.success("AI questions generated successfully")
    } catch (_error) {
      toast.error("Failed to generate AI questions")
    } finally {
      setIsGeneratingAi(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] bg-card p-0 gap-0 overflow-hidden border-border shadow-2xl rounded-2xl">
        <DialogHeader className="bg-muted/50 px-6 py-5 border-b border-border">
          <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
            <div className="w-1 h-5 bg-purple-600 rounded-full" />
            Create Video Interview
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-1.5">
            Define questions for the candidate to answer via video recording.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-foreground">
                Questions
              </Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={
                    isGeneratingAi ||
                    !vacancyId ||
                    !organizationId ||
                    questions.filter((q) => q.text && q.text.trim() !== "")
                      .length >= 5
                  }
                  onClick={handleGenerateAi}
                  className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border-indigo-200 shadow-sm h-8"
                >
                  {isGeneratingAi ? (
                    <Spinner className="size-3.5 mr-2" />
                  ) : (
                    <Bot className="size-3.5 mr-2" />
                  )}
                  {isGeneratingAi
                    ? "Generating..."
                    : "Generate questions with AI"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={questions.length >= 5}
                  onClick={handleAddQuestion}
                  className="bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200 shadow-sm transition-all h-8"
                >
                  <Plus className="size-3.5 mr-1.5" /> Add Question
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {questions.map((question, index) => (
                <div
                  key={index}
                  className="flex gap-3 items-start p-4 border border-border rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors group relative"
                >
                  <div className="mt-3 text-muted-foreground cursor-grab active:cursor-grabbing">
                    <GripVertical className="size-4" />
                  </div>
                  <div className="grid gap-4 flex-1">
                    <div className="grid gap-2">
                      <Label className="text-xs font-medium text-muted-foreground">
                        Question Text
                      </Label>
                      <QuestionCombobox
                        value={question.text || ""}
                        selectedQuestions={
                          questions
                            .map((q) => q.text)
                            .filter(Boolean) as string[]
                        }
                        onChange={(text, id, suggestedTime) => {
                          const newQuestions = [...questions]
                          newQuestions[index] = {
                            ...newQuestions[index],
                            text,
                            id,
                            timeLimitSeconds:
                              suggestedTime ||
                              newQuestions[index].timeLimitSeconds,
                          }
                          setQuestions(newQuestions)
                        }}
                        questions={availableQuestions}
                      />
                    </div>
                    <div className="flex gap-4">
                      <div className="grid gap-2 w-32">
                        <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Clock className="size-3" /> Time Limit (s)
                        </Label>
                        <Input
                          type="number"
                          value={question.timeLimitSeconds}
                          onChange={(e) =>
                            updateQuestion(
                              index,
                              "timeLimitSeconds",
                              e.target.value ? parseInt(e.target.value) : "",
                            )
                          }
                          className="bg-card border-border focus-visible:ring-ring"
                          min={30}
                          step={30}
                        />
                      </div>
                    </div>
                  </div>
                  {questions.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors h-8 w-8"
                      onClick={() => handleRemoveQuestion(index)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="bg-muted/50 px-6 py-4 border-t border-border flex gap-3 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="border-border text-foreground hover:bg-muted rounded-xl px-5"
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || questions.some((q) => !q.text)}
            className="bg-purple-600 hover:bg-purple-700 text-white shadow-md shadow-purple-200 transition-all hover:scale-[1.02] active:scale-[0.98] rounded-xl px-6 font-medium"
          >
            {isSubmitting ? "Sending Invite..." : "Send Invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface QuestionComboboxProps {
  value: string
  selectedQuestions: string[]
  onChange: (text: string, id?: string, suggestedTime?: number) => void
  questions: InterviewQuestion[]
}

function QuestionCombobox({
  value,
  selectedQuestions,
  onChange,
  questions,
}: QuestionComboboxProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState(value)

  useEffect(() => {
    setInputValue(value)
  }, [value])

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-card border-border hover:bg-muted font-normal text-left px-3 h-auto py-2"
        >
          <span className="whitespace-normal wrap-break-word">
            {value || "Select or type a question..."}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-card flex flex-col"
        align="start"
        sideOffset={8}
        style={{
          width: "var(--radix-popover-trigger-width)",
          maxWidth: "var(--radix-popover-trigger-width)",
          maxHeight: "var(--radix-popover-content-available-height)",
        }}
      >
        <div className="p-2 border-b border-border shrink-0">
          <textarea
            className="flex w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 min-h-20 resize-none"
            placeholder="Search or type new question..."
            value={inputValue}
            onChange={(e) => {
              const val = e.target.value
              setInputValue(val)
              onChange(val, undefined)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && inputValue.trim()) {
                e.preventDefault()
                onChange(inputValue.trim(), undefined)
                setOpen(false)
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            disabled={!inputValue || inputValue.trim() === ""}
            onClick={() => {
              onChange(inputValue.trim(), undefined)
              setOpen(false)
            }}
            className="mt-2 w-full bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-medium"
          >
            <Check className="size-3.5 mr-1.5" />
            Confirm Question
          </Button>
        </div>
        <Command
          shouldFilter={false}
          className="flex-1 overflow-hidden min-h-0"
        >
          <CommandList className="max-h-[250px] overflow-y-auto overflow-x-hidden">
            <CommandEmpty>
              {questions.length === 0 &&
                "No questions in your pool yet. Create an interview to start building it up!"}
            </CommandEmpty>
            {questions.length !== 0 && (
              <CommandGroup heading="Suggestions">
                {questions
                  .filter(
                    (q) =>
                      !selectedQuestions.includes(q.text) || q.text === value,
                  )
                  .filter(
                    (q) =>
                      !inputValue ||
                      q.text.toLowerCase().includes(inputValue.toLowerCase()),
                  )
                  .map((q) => (
                    <CommandItem
                      key={q.id}
                      value={q.text}
                      onSelect={() => {
                        onChange(q.text, q.id, q.suggestedTimeSeconds)
                        setOpen(false)
                      }}
                      className="flex items-start py-3 border-b border-dashed border-border last:border-0 rounded-none cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 mt-0.5 h-4 w-4 shrink-0",
                          value === q.text ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="whitespace-normal wrap-break-word flex-1 text-sm text-foreground">
                        {q.text}
                      </span>
                    </CommandItem>
                  ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
